import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { Compass, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { CARIBBEAN_ISLANDS, ISLAND_BY_CODE } from "../../lib/caribbeanIslands";
import { getIslandContextSync } from "../../lib/island-activities-api";
import { mergeDivisionList } from "../../lib/island-divisions";
import { fetchMeetupMapPins } from "../../lib/meetup-map-api";
import { getMeetupMapFallback } from "../../lib/meetup-map-fallback";
import MeetupPinSheet from "../MeetupPinSheet";
import MeetupPinPeek from "./MeetupPinPeek";
import MeetupPinHoverCard from "./MeetupPinHoverCard";
import { createMeetupDivIcon, MEETUP_MAP_CSS } from "./meetupMapIcon";
import { Button } from "../ui/button";
import "leaflet/dist/leaflet.css";

function MapRecenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center?.lat != null && center?.lng != null) {
      map.setView([center.lat, center.lng], zoom || 11);
    }
  }, [center, zoom, map]);
  return null;
}

function MapResizeSync() {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => {
      map.invalidateSize({ animate: false });
    };
    invalidate();
    window.addEventListener("resize", invalidate);
    const observer = new ResizeObserver(invalidate);
    const container = map.getContainer()?.parentElement;
    if (container) observer.observe(container);
    return () => {
      window.removeEventListener("resize", invalidate);
      observer.disconnect();
    };
  }, [map]);
  return null;
}

const HOLD_MS = 3000;
const TAP_MAX_MS = 320;

export default function MeetupSocialMap({
  country: countryProp = "barbados",
  parish: parishProp = "",
  category = "",
  businessView = false,
  variant = "landing",
  mapHeight = 360,
  showIslandPicker = true,
  showParishFilter = true,
  showStats = true,
  showFeaturedStrip = true,
  highlightPinId = null,
  isPro = false,
  onCountryChange,
  onParishChange,
  onPinSelect,
  onOpenChat,
}) {
  const [country, setCountry] = useState(countryProp);
  const [parish, setParish] = useState(parishProp);
  const [pins, setPins] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [divisionLabel, setDivisionLabel] = useState("Parish");
  const [globalStats, setGlobalStats] = useState(null);
  const [pinSource, setPinSource] = useState("api");
  const [center, setCenter] = useState(ISLAND_BY_CODE.barbados.center);
  const [zoom, setZoom] = useState(11);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const initialLoadRef = useRef(true);
  const [selectedPin, setSelectedPin] = useState(null);
  const [peekPin, setPeekPin] = useState(null);
  const [hoverPin, setHoverPin] = useState(null);
  const [isTouch, setIsTouch] = useState(false);
  const holdTimerRef = useRef(null);
  const peekTimerRef = useRef(null);
  const pressStartRef = useRef(0);
  const suppressClickRef = useRef(false);

  const lastExternalCountry = useRef(countryProp);

  useEffect(() => {
    setCountry(countryProp);
    if (lastExternalCountry.current !== countryProp) {
      lastExternalCountry.current = countryProp;
      setParish("");
      setSelectedPin(null);
    }
  }, [countryProp]);

  useEffect(() => {
    setParish(parishProp || "");
  }, [parishProp]);

  useEffect(() => {
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setIsTouch(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const applyInstantIsland = useCallback(
    (code, { parish: parishFilter = parish, category: categoryFilter = category } = {}) => {
      const ctx = getIslandContextSync(code);
      setCenter(ctx.center);
      setZoom(ctx.zoom);
      setDivisionLabel(ctx.division_label);

      const instant = getMeetupMapFallback({
        country: code,
        parish: parishFilter,
        category: categoryFilter,
      });
      const div = mergeDivisionList(code, instant.parishes);
      setPins(instant.pins || []);
      setParishes(div.divisions);
      setDivisionLabel(div.division_label);
      if (instant.stats) setGlobalStats(instant.stats);
      setPinSource("fallback");
    },
    [parish, category]
  );

  useEffect(() => {
    let cancelled = false;
    const isFirst = initialLoadRef.current;
    if (isFirst) {
      setLoading(true);
    } else {
      setRefreshing(true);
      applyInstantIsland(country);
    }

    const load = async () => {
      try {
        const data = await fetchMeetupMapPins({ country, parish, category });
        if (cancelled) return;
        setPins(data.pins || []);
        const div = mergeDivisionList(country, data.parishes || data.divisions || []);
        setParishes(div.divisions);
        setDivisionLabel(data.division_label || div.division_label);
        setGlobalStats(data.stats || null);
        setPinSource(data.source || "api");
        if (data.island_center) setCenter(data.island_center);
        if (data.island_zoom) setZoom(data.island_zoom);
      } catch (err) {
        console.warn("Meetup map pins failed", err);
        if (!cancelled) applyInstantIsland(country);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
          initialLoadRef.current = false;
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [country, parish, category, applyInstantIsland]);

  useEffect(() => {
    if (!highlightPinId || pins.length === 0) return;
    const match = pins.find((p) => p.pin_id === highlightPinId);
    if (match) {
      setSelectedPin(match);
      setCenter({ lat: match.lat, lng: match.lng });
      setZoom((z) => Math.max(z, 13));
    }
  }, [highlightPinId, pins]);

  const clearHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const clearPeek = useCallback(() => {
    if (peekTimerRef.current) {
      clearTimeout(peekTimerRef.current);
      peekTimerRef.current = null;
    }
    setPeekPin(null);
  }, []);

  const activatePin = useCallback(
    (pin) => {
      clearPeek();
      setSelectedPin(pin);
      onPinSelect?.(pin);
    },
    [clearPeek, onPinSelect]
  );

  const handleCountryPick = (code) => {
    setCountry(code);
    setParish("");
    setSelectedPin(null);
    applyInstantIsland(code, { parish: "", category });
    onCountryChange?.(code);
    onParishChange?.("");
  };

  const handleParishPick = (value) => {
    setParish(value);
    onParishChange?.(value);
  };

  const handlePointerDown = useCallback(
    (pin) => {
      if (!isTouch) return;
      pressStartRef.current = Date.now();
      suppressClickRef.current = false;
      clearHold();
      clearPeek();
      holdTimerRef.current = setTimeout(() => {
        suppressClickRef.current = true;
        setPeekPin(pin);
        peekTimerRef.current = setTimeout(() => setPeekPin(null), 4000);
      }, HOLD_MS);
    },
    [isTouch, clearHold, clearPeek]
  );

  const handlePointerUp = useCallback(
    (pin) => {
      if (!isTouch) return;
      const elapsed = Date.now() - pressStartRef.current;
      clearHold();
      if (elapsed < TAP_MAX_MS && !suppressClickRef.current) {
        activatePin(pin);
      }
    },
    [isTouch, clearHold, activatePin]
  );

  const featuredPins = useMemo(
    () => pins.filter((p) => p.is_featured).slice(0, 4),
    [pins]
  );

  const islandLabel = ISLAND_BY_CODE[country]?.name || country.replace("_", " ");
  const heightStyle = useMemo(() => ({ height: `${mapHeight}px`, width: "100%" }), [mapHeight]);

  const isDiscovery = variant === "discovery";

  return (
    <div
      className={`w-full max-w-full overflow-hidden border bg-white shadow-xl ${
        isDiscovery ? "rounded-3xl border-slate-200" : "rounded-[2rem] border-cyan-100"
      }`}
    >
      <style>{MEETUP_MAP_CSS}</style>

      <div className={`border-b border-slate-100 flex flex-col gap-4 ${isDiscovery ? "p-4" : "p-6"}`}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-cyan-600 font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Live geographic map
            </p>
            <h3 className={`font-serif font-bold text-slate-900 ${isDiscovery ? "text-xl" : "text-2xl"}`}>
              {isDiscovery ? `${islandLabel} meet-ups & hotspots` : "Meetup Map"}
            </h3>
          </div>
          <p className="text-sm text-slate-500 max-w-md">
            Real WGS84 pins for every island and parish — businesses, partners, and verified experiences.
            {isTouch
              ? " Tap for details · hold 3 seconds for a quick peek."
              : " Hover for pricing · click to open Map Friend."}
          </p>
        </div>

        {showStats && globalStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: "Caribbean pins", value: globalStats.total_pins },
              { label: "Islands live", value: globalStats.islands },
              { label: "This island", value: pins.length },
              { label: "Parishes", value: parishes.length || "—" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl bg-gradient-to-br from-slate-50 to-cyan-50/80 border border-slate-100 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{stat.label}</p>
                <p className="text-lg font-bold text-slate-900">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {showIslandPicker && (
          <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto scrollbar-thin">
            {CARIBBEAN_ISLANDS.map((island) => (
              <button
                key={island.code}
                type="button"
                onClick={() => handleCountryPick(island.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  country === island.code
                    ? "bg-slate-900 text-white shadow-md"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {island.flag} {island.name}
              </button>
            ))}
          </div>
        )}

        {showParishFilter && parishes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500">{divisionLabel}</span>
            <button
              type="button"
              onClick={() => handleParishPick("")}
              className={`px-3 py-1 rounded-full text-xs ${
                !parish ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              All
            </button>
            {parishes.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleParishPick(p)}
                className={`px-3 py-1 rounded-full text-xs ${
                  parish === p ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative w-full max-w-full overflow-hidden">
        {refreshing && !loading && pins.length > 0 && (
          <div className="absolute top-3 right-3 z-[1000] rounded-full bg-white/90 border border-slate-200 px-3 py-1 text-[10px] font-medium text-slate-600 shadow-sm">
            Syncing live pins…
          </div>
        )}
        {loading ? (
          <div
            className="meetup-map-skeleton flex items-center justify-center text-slate-500"
            style={heightStyle}
          >
            <div className="text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-cyan-400 animate-pulse" />
              Loading live pins…
            </div>
          </div>
        ) : pins.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 bg-slate-50 gap-3"
            style={heightStyle}
          >
            <MapPin className="w-10 h-10 text-cyan-300" />
            <p>No pins for this filter yet.</p>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => handleParishPick("")}>
              Show all parishes
            </Button>
          </div>
        ) : (
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={zoom}
            style={heightStyle}
            className="w-full max-w-full"
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapRecenter center={center} zoom={zoom} />
            <MapResizeSync />
            {pins.map((pin) => {
              const highlighted =
                selectedPin?.pin_id === pin.pin_id ||
                hoverPin?.pin_id === pin.pin_id ||
                peekPin?.pin_id === pin.pin_id ||
                highlightPinId === pin.pin_id;
              return (
                <Marker
                  key={`${pin.kind}-${pin.pin_id}`}
                  position={[pin.lat, pin.lng]}
                  icon={createMeetupDivIcon(pin, { highlighted })}
                  eventHandlers={{
                    click: () => {
                      if (!isTouch) activatePin(pin);
                    },
                    pointerdown: () => handlePointerDown(pin),
                    pointerup: () => handlePointerUp(pin),
                    pointerleave: () => {
                      clearHold();
                    },
                    mouseover: () => {
                      if (!isTouch) setHoverPin(pin);
                    },
                    mouseout: () => {
                      if (!isTouch) setHoverPin(null);
                    },
                  }}
                >
                  <Popup>
                    <div className="min-w-[168px] text-sm">
                      <p className="font-semibold">{pin.name}</p>
                      {pin.parish && <p className="text-xs text-slate-500">{pin.parish}</p>}
                      <p className="text-xs text-cyan-700 mt-1">
                        {isTouch ? "Tap for full details" : "Click for Map Friend"}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}

        {!isTouch && hoverPin && !selectedPin && (
          <MeetupPinHoverCard pin={hoverPin} businessView={businessView} />
        )}
        {peekPin && <MeetupPinPeek pin={peekPin} onDismiss={() => activatePin(peekPin)} />}

        {selectedPin && (
          <>
            <div
              className="fixed inset-0 z-[4999] bg-black/25 md:bg-black/15"
              onClick={() => setSelectedPin(null)}
              aria-hidden
            />
            <MeetupPinSheet
              pin={selectedPin}
              businessView={businessView}
              onClose={() => setSelectedPin(null)}
              onOpenChat={
                onOpenChat
                  ? (pin) => {
                      setSelectedPin(null);
                      onOpenChat(pin);
                    }
                  : undefined
              }
            />
          </>
        )}
      </div>

      {showFeaturedStrip && featuredPins.length > 0 && !loading && (
        <div className="px-4 py-3 border-t border-slate-100 bg-gradient-to-r from-white via-cyan-50/40 to-white">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Featured right now</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {featuredPins.map((pin) => (
              <button
                key={pin.pin_id}
                type="button"
                onClick={() => activatePin(pin)}
                className="shrink-0 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left hover:border-cyan-300 hover:shadow-md transition-all max-w-[200px]"
              >
                <p className="text-xs font-semibold text-slate-900 truncate">{pin.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{pin.parish || pin.island}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 md:px-6 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
          {pins.length} verified geographic pins · {islandLabel}
          {pinSource === "fallback" ? " · live catalog" : ""}
          {isPro ? " · Explorer PRO" : ""}
        </span>
        {isDiscovery ? (
          <span className="inline-flex items-center gap-1 text-cyan-700">
            <Compass className="w-3.5 h-3.5" />
            Synced with Discovery
          </span>
        ) : (
          <Link to={`/discover?country=${country}`} className="text-cyan-700 font-medium hover:underline">
            Explore full island map →
          </Link>
        )}
      </div>
    </div>
  );
}
